import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Database,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Info,
  Layers,
  RefreshCw,
  Loader2,
  Star,
  Eye,
  Trash2
} from 'lucide-react';
import { datasourcesApi } from '../services/datasources.service';
import { hefestoModelsApi, HefestoModel } from '../services/hefestoModels.service';
import HefestoModelView from '../components/HefestoModelView';
import { DataSource } from '../../../types';
import { queryKeys } from '../../../shared/services/queryKeys';

const DataModeling: React.FC = () => {
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [model, setModel] = useState<any>(null);
  const [expandedSource, setExpandedSource] = useState<string | number | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showSavedModels, setShowSavedModels] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [modelName, setModelName] = useState<string>('');
  const [showNameDialog, setShowNameDialog] = useState(false);

  const queryClient = useQueryClient();

  const getErrorMessage = (err: any, fallback: string) =>
    err?.response?.data?.message || err?.message || fallback;

  const datasourcesQuery = useQuery<DataSource[], Error>({
    queryKey: queryKeys.datasources,
    queryFn: datasourcesApi.getDataSources,
  });

  const hefestoModelsQuery = useQuery<{ models: HefestoModel[]; count: number }, Error>({
    queryKey: queryKeys.hefestoModels,
    queryFn: hefestoModelsApi.listModels,
  });

  const createModelMutation = useMutation({
    mutationFn: ({ name, datasourceIds }: { name: string; datasourceIds: string[] }) =>
      hefestoModelsApi.createModel(name, datasourceIds),
    onSuccess: (response) => {
      setModel(response.model.model);
      setModelName('');
      setSelected(new Set());
      queryClient.invalidateQueries({ queryKey: queryKeys.hefestoModels });
      queryClient.invalidateQueries({ queryKey: queryKeys.datasources });
    },
    onError: (err: any) => {
      console.error('Error creating model:', err);
      setError(getErrorMessage(err, 'Error al crear modelo'));
    },
  });

  const deleteModelMutation = useMutation({
    mutationFn: (modelId: string) => hefestoModelsApi.deleteModel(modelId),
    onMutate: (modelId) => {
      setDeletingId(modelId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.hefestoModels });
      queryClient.invalidateQueries({ queryKey: queryKeys.datasources });
    },
    onError: (err: any) => {
      console.error('Error deleting model:', err);
      alert(getErrorMessage(err, 'Error al eliminar el modelo'));
    },
    onSettled: () => {
      setDeletingId(null);
    },
  });

  const loadModelMutation = useMutation({
    mutationFn: (modelId: string) => hefestoModelsApi.getModel(modelId),
    onSuccess: (response) => {
      setModel(response.model.model);
      setSelectedIds(response.model.sourceIds);
    },
    onError: (err: any) => {
      console.error('Error loading model:', err);
      alert(getErrorMessage(err, 'Error al cargar modelo'));
    },
  });

  const datasources = datasourcesQuery.data ?? [];
  const hefestoModels = hefestoModelsQuery.data?.models ?? [];
  const loading = datasourcesQuery.isLoading;
  const generating = createModelMutation.isPending;

  const handleToggleSelect = (id: string | number) => {
    const newSelected = new Set(selected);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelected(newSelected);
  };

  React.useEffect(() => {
    if (!datasourcesQuery.error) return;
    console.error('Error loading datasources:', datasourcesQuery.error);
    setError(getErrorMessage(datasourcesQuery.error, 'Error al cargar fuentes de datos'));
  }, [datasourcesQuery.error]);

  React.useEffect(() => {
    if (!hefestoModelsQuery.error) return;
    console.error('Error loading Hefesto models:', hefestoModelsQuery.error);
  }, [hefestoModelsQuery.error]);

  const handleGenerate = async () => {
    if (selected.size === 0) return;

    const ids = Array.from(selected).map(id => String(id));
    
    // Solicitar nombre del modelo
    setShowNameDialog(true);
    setSelectedIds(ids);
  };

  const handleCreateModel = async () => {
    if (!modelName.trim()) {
      alert('Por favor ingresa un nombre para el modelo');
      return;
    }

    setError(null);
    setShowNameDialog(false);
    createModelMutation.mutate({ name: modelName, datasourceIds: selectedIds });
  };

  const isReady = (ds: DataSource) => {
    return ds.businessDomain && ds.granularity && ds.dataNature;
  };

  const getDomainIcon = (domain?: string | null) => {
    if (!domain) return '📁';
    const icons: Record<string, string> = {
      ventas: '💰',
      costos: '📊',
      clientes: '👥',
      marketing: '📢',
      operaciones: '⚙️',
      finanzas: '💵'
    };
    return icons[domain.toLowerCase()] || '📁';
  };

  const toggleSourceExpansion = (id: string | number) => {
    setExpandedSource(expandedSource === id ? null : id);
  };

  const handleLoadSavedModel = async (modelId: string) => {
    loadModelMutation.mutate(modelId);
  };

  const handleDeleteModel = async (modelId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    const modelToDelete = hefestoModels.find(m => m.id === modelId);
    if (!modelToDelete) return;
    
    if (!confirm(`¿Estás seguro de eliminar el modelo "${modelToDelete.name}"?`)) {
      return;
    }

    deleteModelMutation.mutate(modelId);
  };

  const handleRefreshDatasources = () => {
    setError(null);
    datasourcesQuery.refetch();
  };

  const handleModelSaved = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.datasources });
  };

  // Get datasources that are part of existing models (to exclude from individual badges)
  const datasourcesInModels = new Set(
    hefestoModels.flatMap(model => model.sourceIds)
  );

  if (model) {
    return (
      <HefestoModelView 
        model={model} 
        datasourceIds={selectedIds} 
        onBack={() => setModel(null)} 
        onModelSaved={handleModelSaved}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Modelado Analítico Hefesto
              </h1>
              <p className="text-slate-400 text-sm mt-2">
                Selecciona una o más fuentes de datos para generar un modelo analítico Hefesto
              </p>
            </div>
            <button 
              onClick={handleRefreshDatasources}
              disabled={loading}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
              title="Recargar fuentes"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-500/10 backdrop-blur rounded-2xl border border-red-500/30 p-4 mb-6">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-200">{error}</p>
              </div>
              <button 
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-300"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-12 h-12 text-blue-400 animate-spin mb-4" />
            <p className="text-slate-400">Cargando fuentes de datos...</p>
          </div>
        ) : (
          <>
            {/* Saved Models Section - Now showing independent Hefesto Models */}
            {hefestoModels.length > 0 && (
              <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 backdrop-blur rounded-2xl border border-yellow-500/30 p-6 mb-6 shadow-2xl">
                <button
                  onClick={() => setShowSavedModels(!showSavedModels)}
                  className="w-full flex items-center justify-between mb-4"
                >
                  <h3 className="text-xl font-semibold flex items-center gap-2 text-yellow-300">
                    <Star className="w-6 h-6" />
                    Modelos Hefesto Guardados ({hefestoModels.length})
                  </h3>
                  {showSavedModels ? 
                    <ChevronDown className="w-5 h-5 text-yellow-400" /> : 
                    <ChevronRight className="w-5 h-5 text-yellow-400" />
                  }
                </button>

                {showSavedModels && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {hefestoModels.map(hefesto => {
                      const modelData = hefesto.model as any;
                      const isMultiSource = hefesto.type === 'MULTI_SOURCE';
                      
                      // Confidence badge classes
                      const confClasses = hefesto.confidenceLevel === 'HIGH' 
                        ? 'bg-sky-500/20 text-sky-400' 
                        : hefesto.confidenceLevel === 'MEDIUM' 
                          ? 'bg-yellow-500/20 text-yellow-400' 
                          : 'bg-red-500/20 text-red-400';
                      
                      return (
                        <div key={hefesto.id} className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700 p-4 hover:border-yellow-500/50 transition-colors">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h4 className="font-semibold text-white">{hefesto.name}</h4>
                              <div className="flex items-center gap-2 mt-1">
                                {isMultiSource && (
                                  <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs font-semibold">
                                    MULTI-SOURCE
                                  </span>
                                )}
                                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${confClasses}`}>
                                  {hefesto.confidenceLevel}
                                </span>
                              </div>
                              <p className="text-xs text-slate-400 mt-1">
                                {modelData?.factTables?.length || 0} fact tables • {modelData?.dimensions?.length || 0} dimensiones
                              </p>
                            </div>
                          </div>
                          
                          <p className="text-sm text-slate-400 mb-4 line-clamp-2">
                            {modelData?.businessNarrative?.substring(0, 100)}...
                          </p>
                          
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleLoadSavedModel(hefesto.id)}
                              className="flex-1 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
                            >
                              <Eye className="w-4 h-4" />
                              Ver Modelo
                            </button>
                            <button
                              onClick={(e) => handleDeleteModel(hefesto.id, e)}
                              disabled={deletingId === hefesto.id}
                              className="bg-red-500/20 hover:bg-red-500/30 disabled:bg-slate-700 disabled:cursor-not-allowed text-red-400 px-4 py-2 rounded-lg flex items-center justify-center transition-colors"
                              title="Eliminar modelo"
                            >
                              {deletingId === hefesto.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Data Sources */}
            <div className="bg-slate-800/50 backdrop-blur rounded-2xl border border-slate-700 p-6 mb-6 shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold flex items-center gap-2">
                  <Database className="w-6 h-6 text-sky-400" />
                  Fuentes de Datos Disponibles ({datasources.length})
                </h3>
              </div>

              {datasources.length === 0 ? (
                <div className="text-center py-12">
                  <Database className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400 mb-2">No hay fuentes de datos disponibles</p>
                  <p className="text-slate-500 text-sm">Crea una fuente de datos primero en "Fuentes de Datos"</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {datasources.map((ds) => {
                    const ready = isReady(ds);
                    const isSelected = selected.has(ds.id);
                    const isExpanded = expandedSource === ds.id;

                    return (
                      <div 
                        key={ds.id} 
                        className={`bg-slate-700/50 rounded-xl border transition-all overflow-hidden ${
                          isSelected 
                            ? 'border-blue-500 shadow-lg shadow-blue-500/20' 
                            : ready 
                              ? 'border-slate-600 hover:border-slate-500' 
                              : 'border-slate-700 opacity-60'
                        }`}
                      >
                        <div className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1">
                              {ready && (
                                <button 
                                  onClick={() => toggleSourceExpansion(ds.id)}
                                  className="text-slate-400 hover:text-white transition-colors"
                                >
                                  {isExpanded ? 
                                    <ChevronDown className="w-5 h-5" /> : 
                                    <ChevronRight className="w-5 h-5" />
                                  }
                                </button>
                              )}
                              {ready && (
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleToggleSelect(ds.id)}
                                  className="w-5 h-5 rounded border-slate-500 text-blue-500 focus:ring-blue-500 focus:ring-offset-slate-800 cursor-pointer"
                                />
                              )}
                              {ready ? (
                                <CheckCircle className="w-5 h-5 text-sky-400" />
                              ) : (
                                <AlertCircle className="w-5 h-5 text-red-400" />
                              )}
                              <span className="text-lg font-medium">{ds.name}</span>
                            </div>
                          </div>

                          {ready ? (
                            <div className="flex flex-wrap gap-2 mt-3 ml-8">
                              <span className="px-3 py-1 bg-orange-500/20 text-orange-300 rounded-full text-xs border border-orange-500/30 flex items-center gap-1">
                                {getDomainIcon(ds.businessDomain)} Dominio: {ds.businessDomain}
                              </span>
                              <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs border border-purple-500/30">
                                Granularidad: {ds.granularity}
                              </span>
                              <span className="px-3 py-1 bg-sky-500/20 text-sky-300 rounded-full text-xs border border-sky-500/30">
                                Naturaleza: {ds.dataNature}
                              </span>
                              {ds.mainMetrics && (
                                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs border border-blue-500/30">
                                  Métricas: {ds.mainMetrics}
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mt-3 ml-8">
                              <div className="flex items-start gap-2">
                                <Info className="w-4 h-4 text-yellow-400 mt-0.5" />
                                <p className="text-sm text-yellow-200">
                                  Falta análisis semántico. Analiza esta fuente primero en "Fuentes de Datos".
                                </p>
                              </div>
                            </div>
                          )}
                        </div>

                        {isExpanded && ready && (
                          <div className="border-t border-slate-600 p-4 bg-slate-800/30">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <h4 className="text-sm font-semibold text-slate-400 mb-2">Métricas Principales</h4>
                                <p className="text-sm text-slate-300">{ds.mainMetrics || 'No especificadas'}</p>
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-slate-400 mb-2">Propósito de Negocio</h4>
                                <p className="text-sm text-slate-300">{ds.businessPurpose || 'No especificado'}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Action Button + Name Dialog - Fixed at bottom */}
            {selected.size > 0 && (
              <div className="flex items-center justify-between bg-slate-800/90 backdrop-blur rounded-2xl border border-slate-700 p-6 shadow-2xl sticky bottom-6">
                <div className="text-slate-400">
                  <span className="font-semibold text-white text-lg">{selected.size}</span> fuente(s) seleccionada(s)
                  {selected.size > 1 && (
                    <span className="ml-2 px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs font-semibold">
                      MULTI-SOURCE
                    </span>
                  )}
                </div>
                <button 
                  onClick={handleGenerate}
                  disabled={generating}
                  className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 disabled:from-slate-600 disabled:to-slate-600 rounded-xl font-semibold text-lg shadow-lg hover:shadow-2xl transition-all transform hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Creando Modelo...
                    </>
                  ) : (
                    <>
                      <Layers className="w-5 h-5" />
                      CREAR MODELO HEFESTO
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Name Dialog Modal */}
            {showNameDialog && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
                <div className="bg-slate-800 rounded-2xl border border-slate-600 p-8 max-w-md w-full mx-4 shadow-2xl">
                  <h3 className="text-2xl font-bold mb-4 text-white">Nombre del Modelo</h3>
                  <p className="text-slate-400 mb-6">
                    Ingresa un nombre descriptivo para este modelo Hefesto
                  </p>
                  <input
                    type="text"
                    value={modelName}
                    onChange={(e) => setModelName(e.target.value)}
                    placeholder="Ej: Análisis de Ventas 2025"
                    className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 mb-6"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleCreateModel();
                      } else if (e.key === 'Escape') {
                        setShowNameDialog(false);
                        setModelName('');
                      }
                    }}
                  />
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowNameDialog(false);
                        setModelName('');
                      }}
                      className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg font-semibold transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleCreateModel}
                      disabled={!modelName.trim() || generating}
                      className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 disabled:from-slate-600 disabled:to-slate-600 rounded-lg font-semibold transition-all disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {generating ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Creando...
                        </>
                      ) : (
                        'Crear Modelo'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Info Card */}
            <div className="mt-6 bg-blue-500/5 backdrop-blur rounded-xl border border-blue-500/20 p-4">
              <p className="text-sm text-slate-400 flex items-center gap-2">
                <Info className="w-4 h-4 text-blue-400" />
                <span>
                  <strong>SINGLE SOURCE:</strong> Un modelo para una fuente. <strong>MULTI-SOURCE:</strong> Un modelo unificado tipo Data Warehouse que combina múltiples fuentes detectando relaciones automáticamente.
                </span>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DataModeling;
